import React, { useEffect } from 'react';
import { ProductOptionInput, ProductType, ProductVariantInput, ProductVariantOptionInput } from '@shopify-multiseller/graphql/partner/types';
import { Card } from '../primitives';
import { Button } from '../primitives';
import { Formik, Form } from 'formik';
import {
    IVariantInitialValuesProps,
    VariantValidationSchema
} from '../../views/Product/types';
import * as yup from 'yup';
import ProductVariantEditorImageSection from './ProductVariantEditorImageSection';
import { ProductVariantEditorPricingInventorySection } from './ProductVariantEditorPricingInventorySection';
import { ProductVariantEditorFeesSection } from './ProductVariantEditorFeesSection';

const VariantEditorValidationSchema = yup.object({
    variant: VariantValidationSchema
});

export interface IProductVariantEditorProps {
    open?: boolean;
    onClose: () => void;
    onSave: (_variant: ProductVariantInput) => void;
    variant: ProductVariantInput;
    productId: string;
    quantity?: number;
    options: ProductOptionInput[];
    variants: ProductVariantInput[];
    disabled?: boolean;
    productType: ProductType;
}

export const ProductVariantEditor: React.FC<React.PropsWithChildren<IProductVariantEditorProps>> = ({
    open = false,
    onClose,
    onSave,
    variant,
    quantity,
    productType,
    disabled
}) => {
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden'; // Prevent parent scroll
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    const handleOnSubmit = (values: IVariantInitialValuesProps) => {
        onSave(values.variant);
        onClose();
    };

    const formatVariantOptions = (variantOptions: ProductVariantOptionInput[] | null | undefined) => {
        if (!variantOptions) return '';
        return variantOptions.map(vo => vo.value).join(' / ');
    };

    return open ? (
        <Formik<IVariantInitialValuesProps>
            initialValues={{
                variant: {
                    ...variant,
                    showSku: !!variant?.sku?.trim()
                }
            }}
            validationSchema={VariantEditorValidationSchema}
            validateOnChange={true}
            validateOnBlur={false}
            onSubmit={handleOnSubmit}
        >
            {props => {
                const { values, dirty, isValid, submitCount } = props;
                const variantTitle = formatVariantOptions(values.variant.variantOptions);

                return (
                    <Form onSubmit={props.handleSubmit}>
                        <div className="fixed inset-0 z-30 w-full h-full overflow-y-auto bg-surface" data-testid="variant-editor">
                            <div className="max-w-5xl pb-16 mx-auto sm:px-6 lg:px-8">
                                <div className="flex items-start justify-between mt-4 mb-8">
                                    <div className="ml-4">
                                        <div className="text-2xl font-bold">{variantTitle}</div>
                                    </div>
                                    <div className="flex mr-4 space-x-2">
                                        <Button onClick={onClose} variant="outline" data-testid="variant-editor-close">
                                            Close
                                        </Button>
                                        {!disabled && (
                                            <Button
                                                onClick={() => props.handleSubmit()}
                                                disabled={!dirty || (submitCount > 0 && !isValid)}
                                                data-testid="variant-editor-update"
                                            >
                                                Update
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap -mx-4">
                                    {/* Left Side (2/3 width) */}
                                    <div className="w-full px-4 space-y-4 lg:w-2/3">
                                        <Card className="overflow-hidden">
                                            <ProductVariantEditorPricingInventorySection
                                                disabled={disabled}
                                                productType={productType}
                                                quantity={quantity}
                                            />
                                        </Card>
                                    </div>
                                    {/* Right Side (1/3 width) */}
                                    <div className="w-full px-4 space-y-4 lg:w-1/3">
                                        <Card className="overflow-hidden">
                                            <ProductVariantEditorImageSection disabled={disabled} />
                                        </Card>
                                        <Card className="overflow-hidden">
                                            <ProductVariantEditorFeesSection
                                                disabled={disabled}
                                            />
                                        </Card>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    ) : null;
};
